import { ErrorMessage, Form, Formik } from 'formik';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Alert } from 'src/components/Alert';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { CircularProgress } from 'src/components/CircularProgress';
import { Container } from 'src/components/Container';
import { Divider } from 'src/components/Divider';
import { FormLabel } from 'src/components/FormLabel';
import { Grid } from 'src/components/Grid';
import { Link } from 'src/components/Link';
import { SignatureCanvas } from 'src/components/SignatureCanvas';
import { Stack } from 'src/components/Stack';
import { StickyCard } from 'src/components/StickyCard';
import { Typography } from 'src/components/Typography';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { externalLinks } from 'src/externalLinks';
import { WaiverFormData } from 'src/models/user/_types';
import { signOutUser } from 'src/models/user/user';
import { SubSection } from 'src/pages/Onboarding/components/SubSection';
import { FormCheckbox } from 'src/shared/FormCheckbox';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import { FormCountrySelect } from 'src/shared/Forms/Selects/CountrySelect';
import api from 'src/utils/ajax';
import { timeZones } from 'src/utils/time-zones';
import toast from 'src/utils/toast';

import ErrorPage from './Error';
import { useBusinessWaiver } from './useBusinessWaiver';

const BusinessWaiver: React.FC = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'businessWaiver' });
  const { t: tMessages } = useTranslation('translation', {
    keyPrefix: 'common.messages',
  });
  const { sessionLoading, session } = useOptionalSession();

  const {
    user,
    userLoading,
    SubmitForm,
    validationSchema,
    currencies,
    subdomain,
    subdomainLoading,
    isSubmitSuccess,
    skipBusinessWaiver,
    skipBusinessWaiverLoading,
    setBrandValue,
  } = useBusinessWaiver();

  const handleSignOut = async () => {
    try {
      await signOutUser();
      window.location.replace(externalLinks.login);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error signing out:', error);
      toast.error(
        api.formatError(
          error,
          tMessages('An error occurred while signing out. Please try again.')
        )
      );
    }
  };

  if (userLoading || sessionLoading || skipBusinessWaiverLoading) {
    return (
      <Container
        maxWidth="xl"
        sx={{
          p: 4,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <CircularProgress size="large" />
      </Container>
    );
  } else if (!user?.id) {
    return <ErrorPage message={t('errors.userNotFound')} type={404} />;
  } else if (!user?.roles?.includes('business')) {
    return (
      <Box
        component="main"
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexGrow: 1,
          py: '80px',
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <ErrorPage
            title={t('errors.incorrectAccount')}
            message={t('errors.incorrectAccountMessage')}
            type={401}
          />
          <Box sx={{ mt: -4 }}>
            <Button variant="prominent" color="primary" onClick={handleSignOut}>
              {t('buttons.logout')}
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  const initialValues: WaiverFormData = {
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    streetAddress: user.streetAddress || '',
    city: user.city || '',
    state: user.state || '',
    zipCode: user.zipCode || '',
    brand: user.platform?.brand || session?.brand || '',
    currency: session?.defaultCurrency || 'usd',
    importExercises: 'false',
    signature: '',
    acceptedWaiver: false,
    subdomain: session?.subdomain || '',
    password: '',
    confirmPassword: '',
    countryAddress: user.countryAddress || '',
  };

  const onCurrentPlatform =
    session &&
    user &&
    user.trainerId === session.trainerId &&
    user.trainerId === user.id;

  return (
    <Container maxWidth="xl" sx={{ p: 4, position: 'relative' }}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={SubmitForm}
      >
        {({ isSubmitting, setFieldValue, submitForm }) => (
          <Form>
            <StickyCard
              title={t('stickyCard.title')}
              subtitle={t('stickyCard.subtitle')}
              actions={
                <Button
                  variant="prominent"
                  color="primary"
                  onClick={submitForm}
                  disabled={isSubmitting || isSubmitSuccess}
                  loading={isSubmitting}
                >
                  {isSubmitSuccess
                    ? t('buttons.submitted')
                    : t('buttons.submit')}
                </Button>
              }
              sx={{
                '& > div:first-of-type': {
                  position: 'sticky',
                  top: 0,
                  zIndex: 1200,
                },
              }}
            >
              <Stack spacing={4} sx={{ pt: 2 }}>
                {/* Personal Information Section */}
                <SubSection
                  title={t('personalInformation')}
                  description={t('personalInfoDescription')}
                >
                  <Grid container spacing={2}>
                    <Grid
                      size={{ xs: 12, sm: 6 }}
                      justifyContent="center"
                      alignItems="center"
                    >
                      <FormTextField
                        label={t('fields.firstName')}
                        name="firstName"
                      />
                    </Grid>
                    <Grid
                      size={{ xs: 12, sm: 6 }}
                      justifyContent="center"
                      alignItems="center"
                    >
                      <FormTextField
                        label={t('fields.lastName')}
                        name="lastName"
                      />
                    </Grid>
                    {user?.needsPasswordReset && (
                      <>
                        <Grid
                          size={{ xs: 12, sm: 6 }}
                          justifyContent="center"
                          alignItems="center"
                        >
                          <FormTextField
                            label={t('fields.password')}
                            name="password"
                            type="password"
                          />
                        </Grid>
                        <Grid
                          size={{ xs: 12, sm: 6 }}
                          justifyContent="center"
                          alignItems="center"
                        >
                          <FormTextField
                            label={t('fields.confirmPassword')}
                            name="confirmPassword"
                            type="password"
                          />
                        </Grid>
                      </>
                    )}
                    <Grid
                      size={{ xs: 12, sm: 12 }}
                      justifyContent="center"
                      alignItems="center"
                    >
                      <FormTextField
                        label={t('fields.timeZone')}
                        name="timeZone"
                        type="select"
                        options={timeZones.map((tz) => ({
                          label: tz.name,
                          value: tz.value,
                        }))}
                      />
                    </Grid>
                    <Grid
                      size={{ xs: 12, sm: 6 }}
                      justifyContent="center"
                      alignItems="center"
                    >
                      <FormTextField
                        label={t('fields.streetAddress')}
                        name="streetAddress"
                      />
                    </Grid>
                    <Grid
                      size={{ xs: 12, sm: 6 }}
                      justifyContent="center"
                      alignItems="center"
                    >
                      <FormTextField label={t('fields.city')} name="city" />
                    </Grid>
                    <Grid
                      size={{ xs: 12, sm: 6 }}
                      justifyContent="center"
                      alignItems="center"
                    >
                      <FormTextField label={t('fields.state')} name="state" />
                    </Grid>
                    <Grid
                      size={{ xs: 12, sm: 6 }}
                      justifyContent="center"
                      alignItems="center"
                    >
                      <FormTextField
                        label={t('fields.zipCode')}
                        name="zipCode"
                      />
                    </Grid>
                    <Grid
                      size={{ xs: 12, sm: 6 }}
                      justifyContent="center"
                      alignItems="center"
                    >
                      <FormCountrySelect
                        label={t('fields.country')}
                        name="countryAddress"
                        useISOCode
                      />
                    </Grid>
                  </Grid>
                </SubSection>

                <Divider />

                {/* Platform Information Section */}
                {!onCurrentPlatform && (
                  <SubSection
                    title={t('platformInformation')}
                    description={t('platformInfoDescription')}
                  >
                    <Grid container spacing={2}>
                      <Grid
                        size={12}
                        justifyContent="center"
                        alignItems="center"
                      >
                        <FormTextField
                          label={t('platformFields.platformBrandName')}
                          name="brand"
                          helperText={t('helperTexts.brandNameHelper')}
                          onChange={(e) => setBrandValue(e.target.value)}
                        />
                      </Grid>
                      <Grid
                        size={12}
                        justifyContent="center"
                        alignItems="center"
                      >
                        <FormTextField
                          label={t('platformFields.defaultCurrency')}
                          name="currency"
                          type="select"
                          options={currencies.map((currency) => ({
                            label: currency.label,
                            value: currency.value,
                          }))}
                        />
                      </Grid>

                      {subdomainLoading ? (
                        <Grid
                          size={12}
                          justifyContent="center"
                          alignItems="center"
                        >
                          <CircularProgress />
                        </Grid>
                      ) : (
                        <Grid
                          size={12}
                          justifyContent="center"
                          alignItems="center"
                        >
                          <Box
                            sx={{
                              p: 2,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              bgcolor: 'background.level1',
                            }}
                          >
                            <Typography variant="subtitle1" gutterBottom>
                              {t('subdomain.title')}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                              {t('subdomain.description')}
                            </Typography>
                            <Alert severity="info" sx={{ mb: 2 }}>
                              {t('subdomain.warning')}
                            </Alert>
                            <Typography variant="body2">
                              <Typography
                                variant="body2"
                                fontWeight="bold"
                                sx={{ display: 'inline' }}
                              >
                                {t('subdomain.preview', {
                                  subdomain: subdomain?.data,
                                })}
                              </Typography>
                            </Typography>
                            <Alert severity="success" sx={{ mt: 2 }}>
                              <Typography variant="body2">
                                {t('subdomain.info')}
                              </Typography>
                            </Alert>
                          </Box>
                        </Grid>
                      )}

                      {!onCurrentPlatform && (
                        <Grid
                          size={12}
                          justifyContent="center"
                          alignItems="center"
                        >
                          <FormTextField
                            label={t('platformFields.importExercise')}
                            name="importExercises"
                            type="select"
                            fullWidth
                            helperText={t('helperTexts.importExerciseHelper')}
                            options={[
                              { label: t('options.yes'), value: 'true' },
                              { label: t('options.no'), value: 'false' },
                            ]}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </SubSection>
                )}

                <Divider />

                {/* Waiver Section */}
                <SubSection
                  title={t('legalAgreement')}
                  description={t('legalAgreementDescription')}
                >
                  <Grid container spacing={2}>
                    {!skipBusinessWaiver?.data.skip && (
                      <Grid
                        size={12}
                        justifyContent="center"
                        alignItems="center"
                      >
                        <FormCheckbox
                          name="acceptedWaiver"
                          color="primary"
                          label={
                            <Trans
                              i18nKey="businessWaiver.legal.acceptConditions"
                              components={{
                                termsLink: (
                                  <Link color="primary" href={externalLinks.termsOfService} target="_blank" rel="noopener noreferrer" />
                                ),
                              }}
                            />
                          }
                          sx={{ p: 1, mb: 2 }}
                        />
                        <ErrorMessage name="acceptedWaiver">
                          {(msg) => (
                            <Typography color="error">{msg}</Typography>
                          )}
                        </ErrorMessage>
                      </Grid>
                    )}

                    <Grid size={12} justifyContent="center" alignItems="center">
                      <FormLabel sx={{ display: 'block', mb: 1 }}>
                        {t('legal.digitalSignature')}
                      </FormLabel>
                      <SignatureCanvas
                        width="100%"
                        height="200px"
                        updateSignature={(signature) =>
                          setFieldValue('signature', signature)
                        }
                      />
                      <ErrorMessage name="signature">
                        {(msg) => <Typography color="error">{msg}</Typography>}
                      </ErrorMessage>
                    </Grid>
                  </Grid>
                </SubSection>
              </Stack>
            </StickyCard>
          </Form>
        )}
      </Formik>
    </Container>
  );
};

export default BusinessWaiver;
